/**
 * Inspired by React Modal Stack
 * Author: Matt Jennings
 * npm: @mattjennings/react-modal-stack
 * github: https://github.com/mattjennings/react-modal-stack
 * License: MIT
 */

import React, {
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import Background from "./Background";
import ModalStack from "./ModalStack";

import type {
  ModalOptions,
  ModalProps,
  ModalStackValue,
} from "./ModalStack.types";

export interface ModalStackProps {
  renderModals?: React.ComponentType<ModalStackValue>;
  children?: React.ReactNode;
}

const defaultContextValue: ModalStackValue = {
  stack: [],
  setModal: async () => [],
  openModal: async () => [],
  replaceModal: async () => [],
  closeModal: async () => [],
  closeModals: () => [],
  closeAllModals: () => [],
  isModalOpen: () => false,
};

const ModalStackContext =
  React.createContext<ModalStackValue>(defaultContextValue);

const ModalStackWrapper = styled.div`
  z-index: 999;
  position: fixed;
`;
const Modals = ({ stack, closeModal }: ModalStackValue) => {
  return (
    <ModalStackWrapper>
      <ModalStack stack={stack} closeModal={closeModal} />
      <Background stack={stack} closeModal={closeModal} />
    </ModalStackWrapper>
  );
};

const ModalProvider = ({ children }: ModalStackProps) => {
  const lastFocusedElementRef = useRef<Element | null>(null);

  const [stack, setStack] = useState<ModalProps[]>([]);
  const pause = React.useRef(false);
  const throttle = 1000;

  const closeAllModals = useCallback(() => {
    setStack([]);
    return stack;
  }, [stack]);

  const closeId = useCallback((id: string) => {
    if (!pause.current) {
      setStack((prev) =>
        [...prev].filter(({ options }) => !(options?.id === id)),
      );
      pause.current = true;
    }
    setTimeout(() => {
      pause.current = false;
    }, throttle);
  }, []);

  const closeModals = useCallback(
    (amount?: number) => {
      if (!pause.current) {
        if (stack.length === 1) {
          closeAllModals();
        } else {
          setStack((prev) => [...prev].slice(0, prev.length - (amount || 1)));
        }
        pause.current = true;
      }

      setTimeout(() => {
        pause.current = false;

        if (
          lastFocusedElementRef.current &&
          "focus" in lastFocusedElementRef.current
        ) {
          (lastFocusedElementRef.current as HTMLElement).focus();
        }
      }, throttle);
      return stack;
    },
    [stack, closeAllModals],
  );

  const openModal = useCallback(
    async (
      data: JSX.Element | null,
      options?: ModalOptions,
      reset?: boolean,
    ) => {
      lastFocusedElementRef.current = document.activeElement;

      const DefaultComponent = () => <div />;
      const { type, props } = data || <DefaultComponent />;
      if (type == null || typeof type === "undefined") {
        throw new Error("Modal type is undefined");
      }
      const modal = {
        type,
        props: { ...options, ...props },
        options: { ...options, ...props },
      };
      const { beforeOpen, afterOpen } = options || {};
      try {
        await beforeOpen?.();
        if (reset) closeAllModals();
        setStack((prev) => {
          let newStack = [...prev];

          if (options?.replace) newStack = stack.slice(0, stack.length - 1);
          // return [...newStack, modal as ModalProps];
          const uniqueIds = new Set();
          const uniqueStack = [...newStack, modal as ModalProps].filter(
            (modal) => {
              if (!uniqueIds.has(modal.options?.id)) {
                uniqueIds.add(modal.options?.id);
                return true;
              }
              return false;
            },
          );
          return uniqueStack.sort(
            (a, b) => (a.options?.zIndex ?? 0) - (b.options?.zIndex ?? 0),
          );
        });
        afterOpen?.();
        /**
         * This is necessary so that on popstate can close the modal.
         * @todo: optional is to pass props to the history state
         */
        window.history.pushState({}, "", "");
        return stack;
      } catch (error) {
        console.error(error);
        return stack;
      }
    },
    [stack, closeAllModals],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const closeModal = useCallback(
    async (props?: { id?: string }) => {
      const { beforeClose, afterClose } =
        stack[stack.length - 1]?.options || {};
      try {
        await beforeClose?.(props);
        if (props?.id) {
          closeId(props?.id);
        } else {
          closeModals(1);
        }
        afterClose?.(props);

        return stack;
      } catch (error) {
        console.error("Error in closeModal:", error);
        const errorMessage =
          error instanceof Error
            ? error.message || "Unknown error"
            : String(error) || "Unknown error";

        console.error(`Modal closing error: ${errorMessage}`);

        if (window.Sentry) {
          window.Sentry.captureException(error);
        }

        return stack;
      }
    },
    [stack, closeId, closeModals],
  );

  const replaceModal = useCallback(
    (data: JSX.Element | null, options?: ModalOptions) => {
      return closeModal().then(() => openModal(data, options));
    },
    [closeModal, openModal],
  );

  const setModal = useCallback(
    (data: JSX.Element | null, options?: ModalOptions) =>
      openModal(data, options, true),
    [openModal],
  );

  const isModalOpen = useCallback(
    (id: string) => stack.some(({ options }) => options?.id === id),
    [stack],
  );

  const value = useMemo(() => {
    return {
      stack,
      setModal,
      openModal,
      replaceModal,
      closeModal,
      closeModals,
      closeAllModals,
      isModalOpen,
    };
  }, [
    stack,
    setModal,
    openModal,
    replaceModal,
    closeModal,
    closeModals,
    closeAllModals,
    isModalOpen,
  ]);

  React.useEffect(() => {
    window.onpopstate = (e: PopStateEvent) => {
      e.preventDefault();
      if (stack.length >= 1) value.closeModal();
    };
  }, [stack, value.closeModal]);

  return (
    <ModalStackContext.Provider value={value} key="modal-stack-provider">
      {children}
      {/* <Modals {...value} /> */}
      {ReactDOM.createPortal(<Modals {...value} />, document.body)}
    </ModalStackContext.Provider>
  );
};

export const useModals = () => useContext(ModalStackContext);
export default ModalProvider;
